(() => {
  const gate = document.getElementById(
    'TroentorpLocationGate'
  );

  if (!gate) return;

  const continueButton = gate.querySelector(
    '[data-location-gate-continue]'
  );

  /*
   * XNEST:
   * If a non-US visitor explicitly chooses to continue
   * on the US store, remember that choice for 24 hours.
   */
  const CONTINUE_US_KEY =
    'troentorp_continue_us_until';

  const CONTINUE_US_TTL =
    24 * 60 * 60 * 1000;

  /*
   * XNEST:
   * Public IP-country providers.
   *
   * country.is is primary.
   * GeoJS is used automatically as fallback.
   */
  const geoProviders = [
    {
      url: 'https://api.country.is/',
      getCountry: (data) => data?.country,
    },
    {
      url: 'https://get.geojs.io/v1/ip/country.json',
      getCountry: (data) => data?.country,
    },
  ];

  const normalizeCountryCode = (country) => {
    if (typeof country !== 'string') {
      return null;
    }

    const normalized =
      country.trim().toUpperCase();

    /*
     * Accept only valid-looking ISO alpha-2
     * country codes.
     */
    if (!/^[A-Z]{2}$/.test(normalized)) {
      return null;
    }

    /*
     * Ignore unknown / placeholder values.
     */
    if (
      normalized === 'XX' ||
      normalized === 'ZZ'
    ) {
      return null;
    }

    return normalized;
  };

  const shouldContinueOnUSStore = () => {
    try {
      const continueUntil = Number(
        localStorage.getItem(
          CONTINUE_US_KEY
        )
      );

      if (!continueUntil) {
        return false;
      }

      if (Date.now() >= continueUntil) {
        localStorage.removeItem(
          CONTINUE_US_KEY
        );

        return false;
      }

      return true;
    } catch (error) {
      /*
       * localStorage may be unavailable in some
       * privacy modes. Continue normal geo detection.
       */
      return false;
    }
  };

  const rememberContinueOnUSStore = () => {
    try {
      const continueUntil =
        Date.now() + CONTINUE_US_TTL;

      localStorage.setItem(
        CONTINUE_US_KEY,
        continueUntil.toString()
      );
    } catch (error) {
      /*
       * Storage failure must never affect the
       * storefront.
       */
    }
  };

  const fetchWithTimeout = async (
    url,
    timeout = 3000
  ) => {
    const controller =
      new AbortController();

    const timeoutId = window.setTimeout(
      () => controller.abort(),
      timeout
    );

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Geo request failed: ${response.status}`
        );
      }

      return await response.json();
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const detectVisitorCountry = async () => {
    /*
     * Do not cache geo detection.
     *
     * This keeps the result accurate when the visitor's
     * public IP changes, including VPN testing.
     */
    for (const provider of geoProviders) {
      try {
        const data = await fetchWithTimeout(
          provider.url
        );

        const country =
          normalizeCountryCode(
            provider.getCountry(data)
          );

        if (country) {
          return country;
        }
      } catch (error) {
        /*
         * Fail silently and try the next provider.
         *
         * A geo provider outage must never prevent
         * visitors from accessing the storefront.
         */
      }
    }

    return null;
  };

  const showGate = (country) => {
    gate.dataset.countryCode = country;

    gate.hidden = false;

    document.documentElement.classList.add(
      'troentorp-location-gate-active'
    );
  };

  const hideGate = () => {
    gate.hidden = true;

    document.documentElement.classList.remove(
      'troentorp-location-gate-active'
    );
  };

  const continueOnUSStore = () => {
    rememberContinueOnUSStore();
    hideGate();
  };

  const initLocationGate = async () => {
    /*
     * Visitor already explicitly chose the US store.
     */
    if (shouldContinueOnUSStore()) {
      return;
    }

    const country =
      await detectVisitorCountry();

    /*
     * Fail open.
     *
     * If the visitor country cannot be reliably
     * determined, do not block access to the store.
     */
    if (!country) {
      return;
    }

    /*
     * US visitor:
     * no popup.
     */
    if (country === 'US') {
      return;
    }

    /*
     * Non-US visitor:
     * show the location notice and allow the visitor
     * to choose either storefront.
     */
    showGate(country);
  };

  if (continueButton) {
    continueButton.addEventListener(
      'click',
      continueOnUSStore
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initLocationGate,
      { once: true }
    );
  } else {
    initLocationGate();
  }
})();